import { isEqual, isNil } from 'lodash';
import { compose } from 'redux';

import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import {
  AssetsType,
  BaseStatus,
  mainAssetsAccountTypes,
  otherAssetsAndCreditsTypes,
  SourceTypeTypes,
} from 'constants/loanApplication';
import {
  assetsRelationshipOptions,
  assetsTypesOptions,
  baseStatusOptions,
  sourceOfFundsOptions,
  sourceTypeOptions,
} from 'constants/options';
import { ItemGroupAlignment, ItemLabelPlacement } from 'constants/ui';
import { getConditionalArray } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import { FinancialInfoTestIds } from '../../../../FinancialInfo.testIds';
import { ownersFieldCustomStyles } from '../../Content.styles';
import { checkboxStyles } from '../SectionContent.styles';
import { getPlaceholder } from '../SectionContent.utils';

import { giftFundsProvisionPeriodOptions, GiftFundsProvisionPeriodType } from './Assets.constants';

export const getConfigFields = ({
  recordId,
  ownersOptions,
  isVerified,
  onChangeAssetType,
  isNewRecord,
}) => ({
  owners: {
    fieldId: 'owners',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].owners`,
    inputProps: {
      placeholder: getPlaceholder(isNewRecord, 'Choose Borrower'),
      disabled: false,
      required: true,
      multiple: true,
      variant: 'outlined',
      isOptionEqualToValue: (option, value) => isEqual(option, value),
      options: ownersOptions,
      'data-test-value': null,
      columns: 4,
      containerCustomStyles: ownersFieldCustomStyles,
      isOneRow: true,
    },
  },
  type: {
    fieldId: 'type',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].type`,
    inputProps: {
      options: assetsTypesOptions,
      customOnChange: onChangeAssetType,
      label: 'Asset Type',
      placeholder: getPlaceholder(isNewRecord, 'Asset Type'),
      required: true,
      disabled: isVerified,
      variant: 'outlined',
      dataTestId: FinancialInfoTestIds.ASSETS_FORM_ASSET_TYPE_SELECT,
      'data-test-value': assetsTypesOptions[0].name,
    },
  },
  financialInstitution: {
    fieldId: 'financialInstitution',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].financialInstitution`,
    inputProps: {
      label: 'Depositor Institution',
      placeholder: getPlaceholder(isNewRecord, 'Depositor Institution'),
      required: true,
      disabled: isVerified,
      variant: 'outlined',
      dataTestId: FinancialInfoTestIds.ASSETS_FORM_FINANCIAL_INSTITUTION_INPUT,
      'data-test-value': 'financial-institution',
      'data-test-edited-value': 'financial-institution-edited',
    },
  },
  accountNumber: {
    fieldId: 'accountNumber',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].accountNumber`,
    inputProps: {
      label: 'Account Number',
      placeholder: getPlaceholder(isNewRecord, 'Account Number'),
      disabled: isVerified,
      variant: 'outlined',
      dataTestId: FinancialInfoTestIds.ASSETS_FORM_ACCOUNT_NUMBER_INPUT,
      'data-test-value': 'account-number',
      'data-test-edited-value': 'account-number-edited',
    },
  },
  cashOrMarketValue: {
    fieldId: 'cashOrMarketValue',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].cashOrMarketValue`,
    inputProps: {
      label: 'Balance',
      placeholder: getPlaceholder(isNewRecord, '1.00'),
      required: true,
      disabled: isVerified,
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
      dataTestId: FinancialInfoTestIds.ASSETS_FORM_CASH_OR_MARKET_VALUE_INPUT,
      'data-test-value': '200000',
      'data-test-formatted-value': '$200,000',
    },
  },
  sourceType: {
    fieldId: 'sourceType',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].sourceType`,
    inputProps: {
      options: sourceTypeOptions,
      label: 'Source',
      placeholder: getPlaceholder(isNewRecord, 'Source'),
      required: true,
      disabled: isVerified,
      variant: 'outlined',
      dataTestId: FinancialInfoTestIds.ASSETS_FORM_SOURCE_TYPE_SELECT,
      'data-test-value': sourceTypeOptions[0].name,
    },
  },
  otherSourceOfGrantDescription: {
    fieldId: 'otherSourceOfGrantDescription',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].otherSourceOfGrantDescription`,
    inputProps: {
      label: 'Other Source Description',
      placeholder: getPlaceholder(isNewRecord, 'Other Source Description'),
      required: true,
      disabled: isVerified,
      variant: 'outlined',
      dataTestId: FinancialInfoTestIds.ASSETS_FORM_OTHER_SOURCE_OF_GRANT_DESCRIPTION_INPUT,
      'data-test-value': 'other-source-of-grant-description',
      'data-test-edited-value': 'other-source-of-grant-description-edited',
    },
  },
  grantSourceName: {
    fieldId: 'grantSourceName',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].grantSourceName`,
    inputProps: {
      label: 'Source Name',
      placeholder: getPlaceholder(isNewRecord, 'Source Name'),
      required: true,
      maxLength: 150,
      disabled: isVerified,
      variant: 'outlined',
      dataTestId: FinancialInfoTestIds.ASSETS_FORM_GRANT_SOURCE_NAME_INPUT,
      'data-test-value': 'grant-source-name',
      'data-test-edited-value': 'grant-source-name-edited',
    },
  },
  gifterName: {
    fieldId: 'gifterName',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].gifterName`,
    inputProps: {
      label: 'Gifter Name',
      placeholder: getPlaceholder(isNewRecord, 'Gifter Name'),
      required: false,
      disabled: isVerified,
      variant: 'outlined',
      dataTestId: FinancialInfoTestIds.ASSETS_FORM_GIFTER_NAME_INPUT,
      'data-test-value': 'gifter-name',
      'data-test-edited-value': 'gifter-name-edited',
    },
  },
  relationship: {
    fieldId: 'relationship',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].relationship`,
    inputProps: {
      options: assetsRelationshipOptions,
      label: 'Relationship',
      placeholder: getPlaceholder(isNewRecord, 'Relationship'),
      required: false,
      disabled: isVerified,
      variant: 'outlined',
      dataTestId: FinancialInfoTestIds.ASSETS_FORM_RELATIONSHIP_SELECT,
      'data-test-value': assetsRelationshipOptions[0].name,
      'data-test-edited-value': assetsRelationshipOptions[1].name,
    },
  },
  sourceOfFunds: {
    fieldId: 'sourceOfFunds',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].sourceOfFunds`,
    inputProps: {
      options: sourceOfFundsOptions,
      label: 'Source Of Funds',
      placeholder: getPlaceholder(isNewRecord, 'Source Of Funds'),
      required: true,
      disabled: isVerified,
      variant: 'outlined',
      dataTestId: FinancialInfoTestIds.ASSETS_FORM_SOURCEOFFUNDS_SELECT,
      'data-test-value': sourceOfFundsOptions[0].name,
      'data-test-edited-value': sourceOfFundsOptions[1].name,
    },
  },
  isDeposited: {
    fieldId: 'isDeposited',
    type: FieldType.RADIO,
    nameInModel: `[${recordId}].isDeposited`,
    inputProps: {
      labelPlacement: ItemLabelPlacement.END,
      align: ItemGroupAlignment.HORIZONTAL,
      radioGroupAlign: ItemGroupAlignment.HORIZONTAL,
      required: true,
      label: 'Is deposited?',
      disabled: isVerified,
      columns: 4,
      options: baseStatusOptions,
      dataTestId: FinancialInfoTestIds.ASSETS_FORM_IS_DEPOSITED_SELECT,
      'data-test-value': BaseStatus.YES,
    },
  },
  isUsedForDownPayment: {
    fieldId: 'isUsedForDownPayment',
    type: FieldType.CHECKBOX,
    nameInModel: `[${recordId}].isUsedForDownPayment`,
    inputProps: {
      label: 'Use asset for down payment',
      disabled: isVerified,
      customStyles: checkboxStyles,
      variant: 'outlined',
      iconProps: {
        height: '20',
        width: '20',
      },
      columns: 4,
      'data-testid': FinancialInfoTestIds.ASSETS_FORM_IS_USED_FOR_DOWN_PAYMENT_CHECKBOX,
      'data-test-value': true,
    },
  },
  isUsedForIncome: {
    fieldId: 'isUsedForIncome',
    type: FieldType.CHECKBOX,
    nameInModel: `[${recordId}].isUsedForIncome`,
    inputProps: {
      label: 'Use asset for income',
      disabled: isVerified,
      customStyles: checkboxStyles,
      variant: 'outlined',
      iconProps: {
        height: '20',
        width: '20',
      },
      columns: 4,
      'data-testid': FinancialInfoTestIds.ASSETS_FORM_IS_USED_FOR_INCOME_CHECKBOX,
      'data-test-value': true,
    },
  },
  giftFundsProvisionPeriod: {
    fieldId: 'giftFundsProvisionPeriod',
    type: FieldType.RADIO,
    nameInModel: `[${recordId}].giftFundsProvisionPeriod`,
    inputProps: {
      labelPlacement: ItemLabelPlacement.END,
      align: ItemGroupAlignment.HORIZONTAL,
      radioGroupAlign: ItemGroupAlignment.HORIZONTAL,
      required: true,
      disabled: isVerified,
      label: 'When will gift funds be provided?',
      columns: 4,
      options: giftFundsProvisionPeriodOptions,
      dataTestId: FinancialInfoTestIds.ASSETS_FORM_GIFT_FUNDS_PROVISION_PERIOD_SELECT,
      'data-test-value': GiftFundsProvisionPeriodType.PRIOR_TO_CLOSING,
    },
  },
  downPayment: {
    fieldId: 'downPayment',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].downPayment`,
    inputProps: {
      type: FieldInputType.CURRENCY,
      minValue: 0,
      required: false,
      disabled: isVerified,
      label: 'Down Payment',
      placeholder: getPlaceholder(isNewRecord, '1.00'),
      variant: 'outlined',
      dataTestId: FinancialInfoTestIds.ASSETS_FORM_DOWN_PAYMENT_VALUE_INPUT,
      'data-test-value': '20000',
      'data-test-formatted-value': '$20,000',
    },
  },
  closingCosts: {
    fieldId: 'closingCosts',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].closingCosts`,
    inputProps: {
      type: FieldInputType.CURRENCY,
      minValue: 0,
      required: false,
      disabled: isVerified,
      label: 'Closing Costs',
      placeholder: getPlaceholder(isNewRecord, '1.00'),
      variant: 'outlined',
      dataTestId: FinancialInfoTestIds.ASSETS_FORM_CLOSING_COSTS_VALUE_INPUT,
      'data-test-value': '20000',
      'data-test-formatted-value': '$20,000',
    },
  },
  includeInAssets: {
    fieldId: 'includeInAssets',
    type: FieldType.CHECKBOX,
    nameInModel: `[${recordId}].includeInAssets`,
    inputProps: {
      label: 'Include In Assets',
      disabled: isVerified,
      variant: 'outlined',
      customStyles: checkboxStyles,
      iconProps: {
        height: '20',
        width: '20',
      },
      columns: 4,
      'data-testid': FinancialInfoTestIds.ASSETS_FORM_IS_INCLUDE_IN_ASSETS_CHECKBOX,
      'data-test-value': true,
    },
  },
});

export const getConfigFlags = ({ type, sourceType }) => ({
  isGrantType: type === AssetsType.GRANT,
  isAssetTypeGift: type === AssetsType.GIFT,
  isGiftOfPropertyEquity: type === AssetsType.GIFT_OF_PROPERTY_EQUITY,
  isMainAssetType: mainAssetsAccountTypes.includes(type),
  isOtherAssetType: otherAssetsAndCreditsTypes.includes(type),
  isSourceTypeOther: sourceType === SourceTypeTypes.OTHER,
});

export const getConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    recordId,
    ownersOptions,
    type,
    isPurchaseFlow,
    isRefinanceFlow,
    isAssetTypeSourceOfGrantEnabled,
    isDeposited,
    sourceType,
    isVerified,
    onChangeAssetType,
    isNewRecord,
    isOwnersClicked,
  }) => {
    const assetsConfigFields = getConfigFields({
      recordId,
      ownersOptions,
      isVerified,
      onChangeAssetType,
      isNewRecord,
    });

    const {
      isGrantType,
      isGiftOfPropertyEquity,
      isAssetTypeGift,
      isMainAssetType,
      isOtherAssetType,
      isSourceTypeOther,
    } = getConfigFlags({ type, sourceType });

    return {
      groups: [
        ...getConditionalArray(isOwnersClicked || isNewRecord, [
          {
            type: FieldType.GROUP,
            fields: [assetsConfigFields.owners],
            key: `${recordId}-owners`,
          },
        ]),
        {
          type: FieldType.GROUP,
          fields: [
            {
              ...assetsConfigFields.type,
              inputProps: {
                ...assetsConfigFields.type.inputProps,
                options: assetsTypesOptions,
              },
            },
            ...getConditionalArray(isMainAssetType, [
              assetsConfigFields.financialInstitution,
              assetsConfigFields.accountNumber,
              assetsConfigFields.cashOrMarketValue,
            ]),

            ...getConditionalArray(isAssetTypeGift || isGiftOfPropertyEquity, () => [
              assetsConfigFields.gifterName,
              ...(isGiftOfPropertyEquity
                ? [assetsConfigFields.sourceOfFunds]
                : [assetsConfigFields.relationship]),
              {
                ...assetsConfigFields.cashOrMarketValue,
                inputProps: {
                  ...assetsConfigFields.cashOrMarketValue.inputProps,
                  label: isAssetTypeGift ? 'Total Amount' : 'Amount',
                },
              },

              ...getConditionalArray(isAssetTypeGift && (isPurchaseFlow || isRefinanceFlow), () => [
                assetsConfigFields.closingCosts,
                ...getConditionalArray(isPurchaseFlow, [assetsConfigFields.downPayment]),
              ]),
              assetsConfigFields.isDeposited,
              {
                ...assetsConfigFields.giftFundsProvisionPeriod,
                inputProps: {
                  ...assetsConfigFields.giftFundsProvisionPeriod.inputProps,
                  disabled: isVerified || isNil(isDeposited) || isDeposited,
                },
              },
            ]),

            ...getConditionalArray(isGrantType, [assetsConfigFields.grantSourceName]),

            ...getConditionalArray(isGrantType && isAssetTypeSourceOfGrantEnabled, () => [
              assetsConfigFields.sourceType,

              ...getConditionalArray(isSourceTypeOther, [
                assetsConfigFields.otherSourceOfGrantDescription,
              ]),
            ]),

            ...getConditionalArray(isOtherAssetType, [
              {
                ...assetsConfigFields.cashOrMarketValue,
                inputProps: {
                  ...assetsConfigFields.cashOrMarketValue.inputProps,
                  label: isGrantType
                    ? 'Total Amount'
                    : assetsConfigFields.cashOrMarketValue.inputProps.label,
                },
              },
            ]),

            ...getConditionalArray(isGrantType, () => [
              ...getConditionalArray(isPurchaseFlow || isRefinanceFlow, [
                assetsConfigFields.closingCosts,
              ]),
              ...getConditionalArray(isPurchaseFlow, [assetsConfigFields.downPayment]),
            ]),

            ...getConditionalArray(isPurchaseFlow && !isGrantType && !isAssetTypeGift, [
              assetsConfigFields.isUsedForDownPayment,
            ]),
            assetsConfigFields.isUsedForIncome,

            ...getConditionalArray(isGrantType || isAssetTypeGift, [
              assetsConfigFields.includeInAssets,
            ]),
          ],
          key: `${recordId}-assetInfo`,
        },
      ],
    };
  },
);
